import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Returns the global feature-flag map for the logged-in user.
 * Refetches when the user changes (login / logout).
 *
 * IMPORTANT: `loading` stays true while auth is still resolving. This
 * prevents <FeatureGate> from redirecting an authenticated user away on a
 * hard reload of a flag-guarded route before AuthContext finishes booting.
 */
export function useFeatureFlags() {
  const { user, loading: authLoading } = useAuth();
  const isAdmin = user?.role === "admin";
  const [flags, setFlags] = useState({ store_paperwork_enabled: false });
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    // Wait for auth to settle. Until then we keep `loading=true` so the gate
    // shows a spinner instead of redirecting prematurely.
    if (authLoading) return;
    if (!user) {
      setFlags({ store_paperwork_enabled: false });
      setLoading(false);
      return;
    }
    try {
      const res = await axios.get(`${API}/feature-flags`);
      setFlags(res.data || {});
    } catch {
      // Silent — keep defaults so UI doesn't break.
    } finally {
      setLoading(false);
    }
  }, [user, authLoading]);

  useEffect(() => { refresh(); }, [refresh]);

  return { flags, isAdmin, loading: loading || authLoading, refresh };
}
